/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IAny,
  ICarbonScopeDetailWithLoaderContent,
  IDetailComponentProps,
  IDetailComponentPropsTwoSeparate,
  ICarbonScopeOneData
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonScopeOneDetailsComponent from '../CarbonScopeOneDetailWidget/CarbonScopeOneDetailsComponent';
import { EnergyUsageDataSourceType } from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';

const CarbonScopeTwoDetailContent = (
  props: ICarbonScopeDetailWithLoaderContent
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t] = useTranslation();
  const { data, onRefresh } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <CarbonScopeOneDetailsComponent
        data={data as ICarbonScopeOneData}
        trendChartCategory={t(e.SCOPE2)}
        forSummary={props.forSummary}
        forGraph={props.forGraph}
        carbonAllSiteMonthData={props.carbonAllSiteMonthData}
        dataSourceType={props.dataSourceType}
      />
    </div>
  );
};

const CarbonScopeTwoDetailWithLoader = withLoadingStatus(
  withErrorStatus(CarbonScopeTwoDetailContent)
);

const CarbonScopeTwoDetailWidgetSeparatePortfolio = (
  props: IDetailComponentPropsTwoSeparate
) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const {
    dataConnector,
    dataConnectorForGraph,
    dataConnectorForTable,
    dataSourceType,
    requestContext,
    isSite
  } = props;

  const apiContext = useContext(ApiContext);
  const { cemsEnableCarbonSourceSelect } = apiContext;

  const onRefreshPerf = () =>
    dataConnectorForGraph &&
    dataConnectorForGraph.actions &&
    dataConnectorForGraph.actions.getAll();

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const { all, loading, error, status } = dataConnector.data;
  const carbonScopeTwoData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonScopeTwoData ? true : false;

  const allPerf = dataConnectorForGraph?.data.all;
  const loadingPerf = dataConnectorForGraph.data.loading;
  const errorPerf = dataConnectorForGraph.data.error;
  const statusPerf = dataConnectorForGraph.data.status;
  const graphData = (allPerf as unknown) as ICarbonScopeOneData;
  const isDataAvailableForGraph =
    graphData.monthWiseAggregations && graphData.monthWiseAggregations.length
      ? true
      : false;

  const allTable = dataConnectorForTable?.data.all;
  const summaryData = (allTable as unknown) as ICarbonScopeOneData;
  const isDataAvailableForTable =
    summaryData.breakdownBySite && summaryData.breakdownBySite.length
      ? true
      : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonScopeOneData);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [dataPerf, setDataPerf] = useState({} as ICarbonScopeOneData);
  const [checkAllSitesData, setCheckAllSitesData] = useState<boolean>(true);
  const [checkAllMonthData, setCheckAllMonthData] = useState<boolean>(true);
  const [carbonAllSiteMonthData, setCarbonAllSiteMonthData] = useState<boolean>(
    true
  );

  const checkIfAllSiteHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalActualEmission !== 0 &&
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalActualEmission !== null &&
            item.totalPreviousPeriodEmission !== null
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalPreviousPeriodEmission !== null &&
            item.totalActualEmission !== 0 &&
            item.totalActualEmission !== null
        ))
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        let tempData: IAny = JSON.parse(JSON.stringify(carbonScopeTwoData));
        setData(tempData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (isDataAvailableForGraph || errorPerf || statusPerf || !loadingPerf) {
      if (isDataAvailableForGraph) {
        let tempData: IAny = JSON.parse(JSON.stringify(graphData));
        setCheckAllMonthData(
          checkIfAllMonthsHasPreviousPeriod(tempData) &&
            checkIfAllMonthHasCompleteData(tempData)
        );
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForGraph, statusPerf, loadingPerf]);

  useEffect(() => {
    if (isDataAvailableForTable) {
      let tempData: any = summaryData;
      setCheckAllSitesData(
        checkIfAllSitesHasPreviousPeriod(tempData) &&
          checkIfAllSiteHasCompleteData(tempData)
      );
    }
  }, [isDataAvailableForTable]);

  useEffect(() => {
    setCarbonAllSiteMonthData(checkAllSitesData && checkAllMonthData);
  }, [checkAllSitesData, checkAllMonthData]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <span className="emaas-summary-card-header">
            {t(d.SCOPETWOTITLE)}
          </span>
          <Tooltip
            element={
              <Icon
                root="common"
                name="badge-info"
                exactSize="0.875rem"
                className="emaas-summary-card-header-info-icon"
              />
            }
            event="hover"
            // content={t(d.SCOPETWOTOOLTIP)}
            content={
              requestContext?.context.subscriptionType === 'BASIC_SUBSCRIPTION'
                ? t(d.SCOPETWOTOOLTIP)
                : cemsEnableCarbonSourceSelect
                ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                  ? t(d.SCOPETWOTOOLTIPdUTILITY)
                  : dataSourceType ===
                    EnergyUsageDataSourceType.UTILITY_INTERVAL
                  ? t(d.SCOPETWOTOOLTIPINTERVAL)
                  : t(d.SCOPETWOTOOLTIPLIVE)
                : t(d.SCOPETWOTOOLTIP)
            }
            position="right center"
            size="small"
          />
        </Card.Header>
        {statusCode == 204 || statusCodePerf == 204 ? (
          <Card.Content className="detail-summary-card">
            <CarbonScopeTwoDetailWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(e.LOADING)}
              title={t(e.CONNECTIONERRORTITLE)}
              noDataMessage={t(e.NODATAAVAILABLE)}
              connectionErrorMessage={t(e.CONNECTIONERROR)}
              forSummary={true}
              carbonAllSiteMonthData={carbonAllSiteMonthData}
            />
          </Card.Content>
        ) : (
          <>
            <Card.Content className="detail-summary-card detail-view">
              <CarbonScopeTwoDetailWithLoader
                loading={isLoading}
                error={errorInfo}
                statusCode={statusCode}
                onRefresh={onRefresh}
                data={data}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                forSummary={true}
                carbonAllSiteMonthData={carbonAllSiteMonthData}
                dataSourceType={props.dataSourceType}
              />
            </Card.Content>
            <Card.Content className="detail-summary-card detail-view">
              <CarbonScopeTwoDetailWithLoader
                loading={isLoadingPerf}
                error={errorInfoPerf}
                statusCode={statusCodePerf}
                onRefresh={onRefreshPerf}
                data={dataPerf}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                forGraph={true}
                carbonAllSiteMonthData={carbonAllSiteMonthData}
                dataSourceType={props.dataSourceType}
              />
            </Card.Content>
          </>
        )}
      </Card>
    </>
  );
};
export default CarbonScopeTwoDetailWidgetSeparatePortfolio;
